import ClassifierGroup from "models/ClassifierGroup";

/**
 * Shared helpers for working with the self-referential ClassifierGroup tree
 * (each group has an optional `parentGroupId`). Used to build cascading group
 * selectors from a flat list of groups.
 */

/** Map key used for top-level (parentless) groups. */
export const ROOT_GROUP_KEY = "__root__";

const parentKey = (group: ClassifierGroup): string =>
    group.parentGroupId ?? ROOT_GROUP_KEY;

/** Index groups by id for O(1) lookups. */
export function buildGroupById(
    groups: ClassifierGroup[],
): Map<string, ClassifierGroup> {
    const byId = new Map<string, ClassifierGroup>();
    for (const group of groups) {
        byId.set(group.id, group);
    }
    return byId;
}

/**
 * Build a parentId -> children map (top-level groups under {@link ROOT_GROUP_KEY}),
 * with each child list sorted by name.
 */
export function buildChildrenMap(
    groups: ClassifierGroup[],
): Map<string, ClassifierGroup[]> {
    const childMap = new Map<string, ClassifierGroup[]>();
    for (const group of groups) {
        const key = parentKey(group);
        const siblings = childMap.get(key) ?? [];
        siblings.push(group);
        childMap.set(key, siblings);
    }
    for (const siblings of childMap.values()) {
        siblings.sort((a, b) => a.name.localeCompare(b.name));
    }
    return childMap;
}

/** Direct children of a group (or top-level groups when `parentId` is null). */
export function getChildren(
    childMap: Map<string, ClassifierGroup[]>,
    parentId: string | null,
): ClassifierGroup[] {
    return childMap.get(parentId ?? ROOT_GROUP_KEY) ?? [];
}

/**
 * Resolve the ordered ancestor path [root, ..., leaf] for a group id by walking
 * `parentGroupId` upward. Cycle-guarded; stops at the first missing/cyclic link
 * and returns whatever valid prefix was found (rooted at the top).
 */
export function getAncestorPath(
    byId: Map<string, ClassifierGroup>,
    leafId?: string | null,
): string[] {
    if (!leafId) return [];

    const reversed: string[] = [];
    const seen = new Set<string>();
    let currentId: string | null | undefined = leafId;

    while (currentId && !seen.has(currentId)) {
        const group = byId.get(currentId);
        if (!group) break;
        seen.add(currentId);
        reversed.push(currentId);
        currentId = group.parentGroupId ?? null;
    }

    return reversed.reverse();
}

/**
 * Validate a (possibly stale) ordered path against the live tree: each entry must
 * be a real parent->child edge starting from a top-level group. Returns the longest
 * valid prefix.
 */
export function validatePath(
    byId: Map<string, ClassifierGroup>,
    path: string[],
): string[] {
    const valid: string[] = [];
    let expectedParent: string | null = null;
    for (const id of path) {
        const group = byId.get(id);
        if (!group) break;
        if ((group.parentGroupId ?? null) !== expectedParent) break;
        valid.push(id);
        expectedParent = id;
    }
    return valid;
}
